// helpers.js

import axios from 'axios';
import { Notify } from 'quasar';
import { Capacitor } from '@capacitor/core';

import store from '@/store/store';

import { FirebaseAuthentication, getIdToken } from '@/firebaseConfig.js';
import { tabOutlineX } from 'quasar-extras-svg-icons/tabler-icons-v3';
import { Sentry } from '@/vendors/Sentry';

const instance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}`,
});

// let tokenCachePromise = null;
// let tokenExpiry = null;
// const TOKEN_EXPIRY_DURATION = 10 * 60 * 1000;

const fetchToken = async () => {
  try {
    if (store.state.currentUser) {
      if (Capacitor.isNativePlatform()) {
        return (await FirebaseAuthentication.getIdToken()).token;
      } else {
        return getIdToken(store.state.currentUser);
      }
    } else {
      return ''; // Handle case where there's no current user
    }
  } catch (err) {
    console.error('Error fetching token:', err);
    Sentry.captureException(err);
    // tokenCachePromise = null;
    tokenExpiry = null; // Set token expiry time
    return '';
  }
};
// const fetchCachedToken = async () => {
//   // Check if token is cached and still valid
//   if (tokenCachePromise && tokenExpiry && new Date() < tokenExpiry) {
//     return tokenCachePromise;
//   }
//   tokenExpiry = new Date(Date.now() + TOKEN_EXPIRY_DURATION); // Set token expiry time
//   tokenCachePromise = fetchToken();
//   return tokenCachePromise;
// };

const RETRY_COUNT = 3; // Number of retry attempts
const INITIAL_RETRY_DELAY = 1000;

const closeAction = { icon: tabOutlineX, color: 'white', size: 'sm', class: 'q-pa-xs' };

const retryRequest = async (requestFn, retries = RETRY_COUNT, delay = INITIAL_RETRY_DELAY) => {
  try {
    return await requestFn();
  } catch (error) {
    console.error(error);
    if (retries <= 0 || [400, 403, 404, 304].includes(error.response?.status)) {
      throw error;
    }
    await new Promise((res) => setTimeout(res, delay));
    return retryRequest(requestFn, retries - 1, delay * 2); // Exponential backoff
  }
};

instance.interceptors.request.use(async (config) => {
  config.headers['platform'] = Capacitor.getPlatform();
  config.headers['version'] = import.meta.env.VITE_VERSION;
  return config;
});

instance.interceptors.request.use(async (config) => {
  const token = await fetchToken();
  config.headers['authorization'] = token ? `Bearer ${token}` : '';
  return config;
});

const ApiRequest = (req) => {
  return retryRequest(() => instance(req));
};

// Generic Success Handler
const SendSuccess = (message) => {
  if (message) {
    Notify.create({
      message: message || 'Success',
      position: 'top',
      type: 'positive',
      actions: [closeAction],
    });
  }
};

// Generic Error Handler
const SendError = (err) => {
  // Console Log Error
  console.error(err);

  // Default Error
  let message = 'Oops, something went wrong! Please try again later.';

  if (typeof err === 'string') {
    message = err;
  }

  if (typeof err === 'object') {
    if (
      typeof err.response === 'object' &&
      typeof err.response.data === 'object' &&
      typeof err.response.data.message === 'string' &&
      err.response.data.message !== ''
    ) {
      message = err.response.data.message;
    } else if (typeof err.message === 'string' && err.message !== '') {
      message = err.message;
    }
  }

  Notify.create({
    message: message || 'Error',
    position: 'top',
    color: 'negative',
    // closeBtn
    actions: [
      closeAction,
      // { label: '', color: 'white', handler: () => {  } },
    ],
  });
};
const SendInfo = (message, params = {}) => {
  if (!message) {
    return;
  }
  return Notify.create({
    message: message,
    position: 'top',
    actions: [closeAction],
    ...params,
  });
};

const GetVersion = () => {
  return `${import.meta.env.VITE_VERSION || '0.0.0'}`;
};

const GetRelease = () => {
  let platform = window.matchMedia('only screen and (max-width: 768px)').matches
    ? 'web_mobile'
    : 'web_desktop';

  if (Capacitor.isNativePlatform() && window.device?.platform) {
    // ios, android
    platform = window.device.platform;
  }

  return `${platform}-${GetVersion()}`;
};

const RouteToLink = (link) => {
  // This function takes a link and converts it to a dynamic link, and routes accordingly

  // Get the user agent of the browser
  const userAgent = navigator?.userAgent || '';

  // TikTok
  // Mozilla/5.0 (Linux; Android 13; Pixel 6 Build/TQ3A.230805.001.A2; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/117.0.0.0 Mobile Safari/537.36 musical_ly_2023101150 JsSdk/1.0 NetType/WIFI Channel/beta AppName/musical_ly app_version/31.1.15 ByteLocale/en ByteFullLocale/en Region/US Spark/1.4.0.5-bugfix AppVersion/31.1.15 BytedanceWebview/d8a21c6

  // Check the user agent and redirect accordingly
  if (/ByteLocale|Instagram/.test(userAgent)) {
    window.location.href = link;
  } else {
    window.location.href = `${process.env.DYNAMIC_URL}/?link=${encodeURIComponent(link)}&apn=${
      process.env.ANDROID_BUNDLE_ID
    }&ibi=${process.env.APPLE_BUNDLE_ID}&isi=${process.env.APPLE_APP_ID}`;
  }
};

const convertBlobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// CONVERT BASE64 TO UINT8ARRAY
function convertBase64ToUint8Array(base64) {
  try {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (e) {
    Sentry.captureException(e);
    return new Uint8Array(0);
  }
}

function DataURLtoFile(dataURL) {
  // Split the data URL at the comma to get the base64 encoded data
  const [header, base64] = dataURL.split(',');
  // Decode the base64 data
  const mime = header.match(/:(.*?);/)[1];
  const u8arr = convertBase64ToUint8Array(base64);
  // Create a blob with the mime type and the decoded data
  return new Blob([u8arr], { type: mime });
  // Return a file object
  // return BlobToFile(blob, filename);
}

function BlobToFile(blob, filename) {
  const fileType = blob.type.split('/')[1];
  return new File([blob], `${filename}.${fileType}`, { type: blob.type });
}

const mimeToExtension = {
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/ogg': 'ogg',
  'audio/aac': 'aac',
  'audio/flac': 'flac',
  'audio/mp4': 'm4a',
  'audio/mp3': 'mp3',
  'audio/aacp': 'm4a',
  'audio/webm': 'weba',
  'audio/3gpp': '3gp',
  'audio/3gpp2': '3g2',
  'video/webm': 'webm', // Included for cases where audio-only WebM files may be used
  'application/octet-stream': 'aac',
};

async function downloadRecording(fileUrl, fileName) {
  const response = await fetch(fileUrl);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName || 'recording'}.${mimeToExtension[blob.type] || 'mp3'}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export {
  ApiRequest,
  SendSuccess,
  SendInfo,
  SendError,
  GetVersion,
  GetRelease,
  RouteToLink,
  DataURLtoFile,
  convertBase64ToUint8Array,
  convertBlobToBase64,
  BlobToFile,
  downloadRecording,
  closeAction,
};
